import React, { useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`session_${Math.random().toString(36).substr(2, 9)}`);

  const sendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = input;
    setInput('');
    setMessages([...messages, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post(
        `${BACKEND_URL}/api/chat`,
        { message: userMessage, session_id: sessionId },
        { headers }
      );

      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      toast.error('Error al enviar mensaje');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="chatbot-widget">
      {isOpen && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-3xl shadow-2xl w-96 h-[500px] flex flex-col" data-testid="chat-window">
          <div className="bg-pink-600 text-white p-4 rounded-t-3xl flex justify-between items-center">
            <div>
              <h3 className="font-bold text-lg" style={{fontFamily: 'Syne, sans-serif'}}>Asistente Virtual</h3>
              <p className="text-sm opacity-90">Estamos aquí para ayudarte</p>
            </div>
            <button onClick={() => setIsOpen(false)} className="hover:bg-pink-700 rounded-full p-2 transition-colors" data-testid="close-chat">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-zinc-900">
            {messages.length === 0 && (
              <div className="text-center text-zinc-400 mt-8">
                <MessageCircle className="w-12 h-12 mx-auto mb-2 text-pink-500" />
                <p>¡Hola! ¿En qué puedo ayudarte?</p>
              </div>
            )}
            {messages.map((msg, index) => (
              <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-2xl ${
                  msg.role === 'user' 
                    ? 'bg-pink-600 text-white' 
                    : 'bg-zinc-800 text-zinc-100 border border-zinc-700'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-zinc-800 border border-zinc-700 p-3 rounded-2xl">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}} />
                    <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{animationDelay: '0.4s'}} />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-zinc-800 bg-zinc-900 rounded-b-3xl">
            <div className="flex space-x-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder="Escribe tu mensaje..."
                className="flex-1 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
                data-testid="chat-input"
              />
              <Button onClick={sendMessage} disabled={loading} className="bg-pink-600 hover:bg-pink-700" data-testid="send-message">
                <Send className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="bg-pink-600 hover:bg-pink-700 text-white rounded-full w-16 h-16 shadow-2xl transition-transform hover:scale-110"
          data-testid="open-chat"
        >
          <MessageCircle className="w-8 h-8" />
        </Button>
      )}
    </div>
  );
};

export default ChatWidget;